/* eslint-disable @typescript-eslint/no-empty-function */
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  styled,
  DialogContent,
  DialogActions,
  Drawer,
  Alert
} from '@mui/material';
import { useState } from 'react';
import { useStores } from '../stores';
import { observer } from 'mobx-react-lite';

import LoadingScreen from 'components/LoadingScreen';
import { personalDataStore } from 'stores/personalDataStore';
import HelpDrawer from 'components/Interview/HelpDrawer';
import VideoPreview from 'components/Interview/VideoPreview';
import { User } from 'types/api';
import { skillsStore } from 'stores/skillsStore';
import { useMediaDevices } from 'hooks/useMediaDevices';

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  '&.outlined': {
    borderColor: theme.palette.grey[300],
    color: theme.palette.grey[600]
  },
  '&.contained': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  }
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius
  }
}));

interface VideoInterviewPageProps {
  type?: 'hr' | 'candidate';
}

const StartVideoInterviewPage: React.FC<VideoInterviewPageProps> = observer(({ type = 'hr' }) => {
  const navigate = useNavigate();
  const { interviewStore } = useStores();
  const [isSkipDialogOpen, setIsSkipDialogOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { devicesResult } = useMediaDevices();

  const handleHelpClose = (skip: boolean) => {
    setIsHelpOpen(false);
    if (!skip) {
      handleStartInterview();
    }
  };

  const handleSkipCancel = () => {
    setIsSkipDialogOpen(false);
  };

  const handleSkipConfirm = () => {
    console.log('');
  };

  const handleStartClick = () => {
    setIsHelpOpen(true);
  };

  const handleStartInterview = async () => {
    try {
      setError(null);
      const position = {
        position: personalDataStore.data.position,
        speciality: personalDataStore.data.speciality as string,
        level: personalDataStore.data.specialityLevel as User['specialityLevel']
      };

      if (!position.position) {
        setError('Не удалось получить позицию');
        return;
      }

      await interviewStore.startInterview(skillsStore.interviewId as string);

      // Проверяем доступность устройств
      if (devicesResult) {
        if (
          !devicesResult.microphone.hasPermission ||
          !devicesResult.camera.hasPermission
        ) {
          setError('Для проведения интервью необходим доступ к микрофону и камере');
          return;
        }
      }

      // Если интервью успешно создано, переходим на страницу записи
      if (interviewStore.currentInterview?.id) {
        navigate(`/video-interview/record/${interviewStore.currentInterview.id}`);
      } else {
        setError('Не удалось создать интервью');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при создании интервью');
    }
  };

  const content = {
    hr: {
      title: 'Видео-интервью',
      mainText: 'Расскажите, какого специалиста вы ищете. ',
      description: 'Мы проанализируем ваши ответы и автоматически сформулируем требования',
      startButtonText: 'Пройти видео-интервью',
      confirmButtonText: 'Подтвердить',
      confirmText:
        'Если вы пропустите этот шаг вам придется заполнять данные о специальности вручную. Мы рекомендуем записать видео. Это займет не более 5 минут.'
    },
    candidate: {
      title: 'Видео-интервью',
      mainText: 'Ответьте на вопросы в формате видео. ',
      description:
        'Мы оценим ваш уровень владения выбранными навыками и подскажем, на что вам необходимо обратить внимание',
      startButtonText: 'Начать интервью',
      confirmButtonText: 'Подтвердить',
      confirmText:
        'Если вы пропустите этот шаг ваша кандидатура будет предложена работодателям в конце списка приоритетности. Мы рекомендуем записать видео. Это займет не более 5 минут.'
    }
  };

  const currentContent = content[type];

  // Если идет загрузка, показываем экран загрузки
  if (interviewStore.loading) {
    return <LoadingScreen label="Создаем интервью..." />;
  }

  return (
    <>
      <Stack spacing={4} sx={{ height: '100%' }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Content */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{
              fontSize: '16px',
              lineHeight: '24px'
            }}
          >
            {currentContent.mainText} {currentContent.description}
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          {/* <WebcamViewer /> */}
          <Typography
            textAlign="center"
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, mb: 4 }}
          >
            Убедитесь, что ваше лицо не выходит за пределы указанных границ.{' '}
            <Button
              variant="text"
              onClick={() => setIsHelpOpen(true)}
              sx={{ p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
            >
              Посмотреть подробную инструкцию
            </Button>{' '}
            по записи видеоинтервью
          </Typography>
          <VideoPreview isPreview={true} hasVideoAccess={true} isRecording={false} />
        </Box>
        {/* Actions */}
        <Stack spacing={2} sx={{ mt: 'auto !important' }}>
          <StyledButton
            variant="contained"
            fullWidth
            onClick={handleStartClick}
            disabled={!!interviewStore.loading}
          >
            {currentContent.startButtonText}
          </StyledButton>
        </Stack>
      </Stack>

      {/* Skip Confirmation Dialog */}
      <HelpDrawer open={isHelpOpen} onClose={handleHelpClose} />
      <StyledDrawer anchor="bottom" open={isSkipDialogOpen} onClose={handleSkipCancel}>
        <DialogContent>
          <Typography sx={{ mb: 5 }} textAlign="center" fontWeight="600" variant="h6">
            Внимание!
          </Typography>
          <Typography variant="body1" color="text.primary">
            {currentContent.confirmText}
          </Typography>
        </DialogContent>
        <DialogActions>
          <StyledButton variant="contained" fullWidth onClick={handleSkipConfirm}>
            {currentContent.confirmButtonText}
          </StyledButton>
        </DialogActions>
      </StyledDrawer>
    </>
  );
});

export default StartVideoInterviewPage;
